"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { IonContent, IonPage } from "@ionic/react"
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
} from "@mui/material"
import {
  Add as AddIcon,
  SwapHoriz as TransferIcon,
  Refresh as RefreshIcon,
  TrendingUp,
  Inventory,
  LocationOn,
  Close as CloseIcon,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import type { Movement, StockItem } from "../../models/stocks"
import StockMovementList from "./stockmovementlist"
import TransferForm from "./transfer-form"
import { getStocksTransfer, getTlocations, transferStocks } from "../../api/stock-transfer/stock-transfer"
import { getProducts } from "../../api/products/product"
import { getVendors } from "../../api/vendor/vendor"

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  border: "1px solid rgba(0,0,0,0.06)",
}))

const HeaderCard = styled(Box)(({ theme }) => ({
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  padding: theme.spacing(3),
  borderRadius: "12px 12px 0 0",
}))

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  textAlign: "center",
  borderRadius: 8,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
}))

const Transfers: React.FC = () => {
  const [openModal, setOpenModal] = useState(false)
  const [transfers, setTransfers] = useState<Movement[]>([])
  const [stockItems, setStockItems] = useState<StockItem[]>([])
  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  })

  const locations =[]= ["Warehouse A", "Warehouse B", "Bar", "Store", "Kitchen", "Main Storage"]

  const [tlocations , setTlocations] = useState([])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [response1, response3] = await Promise.all([getProducts(), getStocksTransfer()])
      console.log([response1, response3])
      setStockItems(response1.data.data || [])
     // setVendors(response2.data.data || [])
      setTransfers(response3.data.data || [])
 
    } catch (error) {
      console.error(error)
      setToast({
        open: true,
        message: "Failed to fetch data",
        severity: "error",
      })
    } finally {
      setLoading(false)
    }
  }

    const fetchData2 = async () => {
    try {
      setLoading(true)
      const response4 = await getTlocations()
 
      setTlocations(response4.data || [])
      console.log("Tlocations:", response4.data)
    } catch (error) {
      console.error(error)

      setToast({
        open: true,
        message: "Failed to fetch data",
        severity: "error",
      })

    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData();
    fetchData2();
  }, [])

  const handleClose = () => {
    setOpenModal((prev) => !prev)
  }

  const handleSubmitTransfer = async (transfer: any) => {
    try {
      const newTransfer: Movement = {
        id: Date.now().toString(),
        itemId: transfer.itemId,
        itemName: stockItems.find((i) => i.id === transfer.itemId)?.name || "",
        type: "transfer",
        quantity: Number(transfer.quantity),
        fromLocation: transfer.fromLocation,
        toLocation: transfer.toLocation,
        date: transfer.date,
        user: "Current User",
      }

     const responsex = await transferStocks(newTransfer)
     console.log("Transfer response:", responsex)

      setTransfers([newTransfer, ...transfers])
      setToast({
        open: true,
        message: "Transfer completed successfully!",
        severity: "success",
      })
      setOpenModal(false)
      fetchData()
    } catch (error) {
      setToast({
        open: true,
        message: "Failed to complete transfer",
        severity: "error",
      })
    }
  }

  const getTodayTransfers = () => {
    const today = new Date().toDateString()
    return transfers.filter((transfer) => new Date(transfer.date).toDateString() === today).length
  }

  const getTotalQuantityTransferred = () => {
    return transfers.reduce((total, transfer) => total + transfer.quantity, 0)
  }

  const getUniqueLocations = () => {
    const allLocations = new Set([...transfers.map((t) => t.fromLocation), ...transfers.map((t) => t.toLocation)])
    return allLocations.size
  }

  const getRecentTransfers = () => {
    return transfers.slice(0, 5)
  }

  return (
    <IonPage>
      <IonContent>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Header */}
          <StyledCard sx={{ mb: 3 }}>
            <HeaderCard>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight="600"
                    color="#1e293b"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <TransferIcon />
                    
                    Stock Transfers
                  </Typography>
                  <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
                    Manage and track stock movements between locations
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchData}
                    disabled={loading}
                    sx={{ color: "#64748b", borderColor: "#d1d5db" }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenModal(true)}
                    sx={{
                      bgcolor: "#1e293b",
                      "&:hover": { bgcolor: "#334155" },
                      textTransform: "none",
                      px: 3,
                    }}
                  >
                    New Transfer
                  </Button>
                </Box>
              </Box>
            </HeaderCard>
          </StyledCard>

          {/* Stats Dashboard */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <TransferIcon sx={{ fontSize: 32, mb: 1, color: "#3b82f6" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {transfers.length}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Total Transfers
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <TrendingUp sx={{ fontSize: 32, mb: 1, color: "#10b981" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getTodayTransfers()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Today's Transfers
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <Inventory sx={{ fontSize: 32, mb: 1, color: "#f59e0b" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {/* {getTotalQuantityTransferred().toLocaleString()} */}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Items Transferred
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <LocationOn sx={{ fontSize: 32, mb: 1, color: "#ef4444" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getUniqueLocations()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Active Locations
                </Typography>
              </StatsCard>
            </Grid>
          </Grid>

          {/* Main Content */}
          {loading ? (
            <StyledCard>
              <CardContent sx={{ py: 8, textAlign: "center" }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="#64748b" sx={{ mt: 2 }}>
                  Loading transfer data...
                </Typography>
              </CardContent>
            </StyledCard>
          ) : (
            <Grid container spacing={3}>
              {/* Transfer History */}
              <Grid item xs={12}>
                <StyledCard>
                  <Box
                    sx={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      padding: 3,
                      borderRadius: "12px 12px 0 0",
                    }}
                  >
                    <Typography variant="h6" fontWeight="600" color="#1e293b">
                      Transfer History
                    </Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
                      Track all stock movements and transfers
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 0 }}>
                    {transfers?.length === 0 ? (
                      <Box sx={{ textAlign: "center", py: 8 }}>
                        <TransferIcon sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }} />
                        <Typography variant="h6" color="#64748b" sx={{ mb: 1 }}>
                          No Transfers Found
                        </Typography>
                        <Typography variant="body2" color="#94a3b8" sx={{ mb: 3 }}>
                          Create your first stock transfer to get started
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => setOpenModal(true)}
                          sx={{
                            bgcolor: "#1e293b",
                            "&:hover": { bgcolor: "#334155" },
                            textTransform: "none",
                          }}
                        >
                          Create Transfer
                        </Button>
                      </Box>
                    ) : (
                      <StockMovementList movements={transfers} />
                    )}
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {/* Transfer Form Modal */}
          <TransferForm
            items={stockItems}
            locations={tlocations}
           // tlocations={tlocations}
            onSubmit={handleSubmitTransfer}
            handleClose={handleClose}
            vendors={vendors}
            openModal={openModal}
            userRole="warehouse"
          />

          {/* Toast Notifications */}
          <Snackbar
            open={toast.open}
            autoHideDuration={4000}
            onClose={() => setToast({ ...toast, open: false })}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={() => setToast({ ...toast, open: false })}
              severity={toast.severity}
              variant="filled"
              sx={{ width: "100%" }}
              action={
                <IconButton size="small" onClick={() => setToast({ ...toast, open: false })}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {toast.message}
            </Alert>
          </Snackbar>
        </Container>
      </IonContent>
    </IonPage>
  )
}

export default Transfers
